/**
 * TicketVisitBanner — banner shown at the top of a ticket chat when the
 * ticket is linked to a visit. Tap routes to the visit detail screen; when
 * the visit is no longer viewable (RN unassigned and the visit isn't
 * pending+broadcasted), the parent shows a snackbar via `onPress`.
 *
 * It says plainly which visit the chat is about (Noel's list item 29): the
 * patient (as much of the name as the API allows: "Indira Imran", "Indira I."
 * or "Patient I.I."), the date
 * and time, where it is, and a note when the visit was cancelled or is no
 * longer hers. Text comes from lib/chat-visit-context.ts.
 */

import { MaterialCommunityIcons } from "@expo/vector-icons";
import { StyleSheet, TouchableOpacity, View } from "react-native";
import { Text } from "react-native-paper";

import type { VisitPreview } from "@/lib/api";
import { summarizeVisitPreview } from "@/lib/chat-visit-context";
import { colors, fonts, missedVisitColors, radius, spacing, typography } from "@/lib/theme";

interface Props {
    preview: VisitPreview;
    onPress: () => void;
}

export function TicketVisitBanner({ preview, onPress }: Props) {
    const summary = summarizeVisitPreview(preview);
    const cancelled = preview.status === "cancelled";

    return (
        <TouchableOpacity
            style={styles.banner}
            onPress={onPress}
            accessibilityRole="button"
            accessibilityLabel={[
                `This chat is about the visit for ${summary.name}`,
                summary.when,
                summary.location,
                summary.statusNote
            ]
                .filter(Boolean)
                .join(", ")}
        >
            <View style={styles.iconBubble}>
                <MaterialCommunityIcons name="calendar-clock" size={18} color={colors.teal400} />
            </View>
            <View style={styles.textCol}>
                <View style={styles.labelRow}>
                    <Text numberOfLines={1} style={styles.label}>
                        THIS CHAT IS ABOUT
                    </Text>
                    {summary.statusNote ? (
                        <View
                            style={[
                                styles.statusPill,
                                cancelled ? styles.statusPillCancelled : styles.statusPillNotYours
                            ]}
                        >
                            <Text
                                numberOfLines={1}
                                style={[
                                    styles.statusText,
                                    cancelled
                                        ? styles.statusTextCancelled
                                        : styles.statusTextNotYours
                                ]}
                            >
                                {summary.statusNote}
                            </Text>
                        </View>
                    ) : null}
                </View>
                <Text numberOfLines={1} style={styles.name}>
                    {summary.name}
                </Text>
                <Text numberOfLines={1} style={styles.when}>
                    {summary.when}
                </Text>
                {summary.location ? (
                    <View style={styles.locationRow}>
                        <MaterialCommunityIcons
                            name="map-marker-outline"
                            size={14}
                            color={colors.textSecondary}
                        />
                        <Text numberOfLines={2} style={styles.location}>
                            {summary.location}
                        </Text>
                    </View>
                ) : null}
            </View>
            <MaterialCommunityIcons name="chevron-right" size={20} color={colors.textSecondary} />
        </TouchableOpacity>
    );
}

const styles = StyleSheet.create({
    banner: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.sm,
        backgroundColor: colors.surface,
        paddingHorizontal: spacing.base,
        paddingVertical: spacing.sm,
        borderBottomWidth: 1,
        borderBottomColor: colors.border
    },
    iconBubble: {
        width: 32,
        height: 32,
        borderRadius: radius.sm,
        backgroundColor: colors.teal50,
        alignItems: "center",
        justifyContent: "center",
        alignSelf: "flex-start",
        marginTop: 2
    },
    textCol: {
        flex: 1,
        minWidth: 0
    },
    labelRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.sm
    },
    label: {
        ...typography.label,
        color: colors.textSecondary,
        flexShrink: 1
    },
    statusPill: {
        paddingHorizontal: spacing.sm,
        paddingVertical: 1,
        borderRadius: radius.full
    },
    // Text/background pairs that pass WCAG AA (4.78:1 and 7.76:1).
    statusPillCancelled: { backgroundColor: missedVisitColors.background },
    statusPillNotYours: { backgroundColor: colors.gray100 },
    statusText: {
        ...typography.caption,
        fontFamily: fonts.semiBold,
        fontSize: 11,
        lineHeight: 16
    },
    statusTextCancelled: { color: missedVisitColors.text },
    statusTextNotYours: { color: colors.gray700 },
    name: {
        ...typography.body,
        fontFamily: fonts.semiBold,
        color: colors.textPrimary
    },
    when: {
        ...typography.bodySmall,
        color: colors.textPrimary
    },
    locationRow: {
        flexDirection: "row",
        alignItems: "flex-start",
        gap: 2,
        marginTop: 1
    },
    location: {
        ...typography.caption,
        color: colors.textSecondary,
        flex: 1
    }
});
